// Cartas (cópia de trabalho): filtros iguais aos da galeria do site + tabela com o estado "por publicar".
import { useApi } from "@ecliptica/api-client/react";
import { CardFilters, CardImage, EMPTY_FILTER, filterCards, type CardFilter, type Catalog, type CatalogCard } from "@ecliptica/game-ui";
import { activeLangOr, LANGS, useTranslation, type Lang } from "@ecliptica/i18n";
import { Badge, Button, Card, PageHeader, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ecliptica/ui";
import { useQuery } from "@tanstack/react-query";
import { Rocket } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useCan } from "../../features/auth/RequireStaff";

type Row = { id: string; editionCode: string; kind: string; def: Record<string, unknown>; texts: Record<string, Record<string, string>>; changed: boolean };

export function CardsPage() {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const can = useCan();
  const lang = activeLangOr(i18n.language);
  const [filter, setFilter] = useState<CardFilter>(EMPTY_FILTER);
  const list = useQuery({
    queryKey: ["content-cards"],
    queryFn: async () => (await (await api.v1.admin.content.cards.$get()).json()) as unknown as Row[],
  });

  // A mesma forma que a galeria do site usa (filtros e pesquisa partilhados em @ecliptica/game-ui).
  const catalog = useMemo<Catalog | null>(() => {
    if (!list.data) return null;
    const codes = [...new Set(list.data.map((r) => r.editionCode))];
    const text = (r: Row, l: Lang) => ({ name: r.texts[l]?.name ?? r.id, tribe: r.texts[l]?.tribe ?? "", text: r.texts[l]?.text ?? "", flavor: r.texts[l]?.flavor ?? "" });
    return {
      editions: codes.map((code) => ({ code, kind: "", size: 0, ...(Object.fromEntries(LANGS.map((l) => [l, code])) as Record<Lang, string>) })),
      cards: list.data.map((r) => ({ ...(r.def as unknown as CatalogCard), ed: r.editionCode, ...(Object.fromEntries(LANGS.map((l) => [l, text(r, l)])) as Record<Lang, ReturnType<typeof text>>) })),
    };
  }, [list.data]);
  const rows = useMemo(() => {
    if (!catalog || !list.data) return [];
    const ids = new Set(filterCards(catalog.cards, filter, lang).map((c) => c.id));
    return list.data.filter((r) => ids.has(r.id));
  }, [catalog, list.data, filter, lang]);
  const pending = list.data?.filter((r) => r.changed).length ?? 0;

  return (
    <>
      <PageHeader
        title={t("nav.cards")}
        description={t("content.description")}
        actions={
          can("content.read") && (
            <Button asChild variant={pending ? "primary" : "outline"}>
              <Link to="/cards/publish">
                <Rocket /> {t("content.publish")} {pending ? `(${pending})` : ""}
              </Link>
            </Button>
          )
        }
      />
      {catalog && (
        <div className="mb-4">
          <CardFilters catalog={catalog} lang={lang} value={filter} onChange={setFilter} />
        </div>
      )}
      <Card>
        {!list.data ? (
          <Skeleton className="m-4 h-40" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("content.col.card")}</TableHead>
                <TableHead className="hidden md:table-cell">{t("content.col.type")}</TableHead>
                <TableHead className="hidden sm:table-cell">{t("content.col.stats")}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Link to={`/cards/${r.id}`} className="flex items-center gap-3 hover:text-gold-bright">
                      <CardImage id={r.id} lang={lang} alt="" className="w-10 shrink-0 shadow-none" />
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{r.texts[lang]?.name ?? r.id}</span>
                        <span className="text-xs text-muted-foreground">
                          {r.id} · {t(`game.aspect.${String(r.def.aspect)}`)}
                          {r.def.rarity ? ` · ${t(`game.rarity.${String(r.def.rarity)}`)}` : ""}
                        </span>
                      </span>
                    </Link>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {t(`game.type.${String(r.def.type)}`)} {r.kind === "token" && <Badge variant="outline">{t("content.token")}</Badge>}
                  </TableCell>
                  <TableCell className="hidden font-mono text-sm sm:table-cell">
                    {String(r.def.cost)} {r.def.type === "unit" ? `· ${String(r.def.atk)} · ${String(r.def.hp)}` : ""}
                  </TableCell>
                  <TableCell className="text-right">{r.changed && <Badge variant="warning">{t("content.changed")}</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      <p className="mt-3 text-sm text-muted-foreground">{t("content.count", { count: rows.length })}</p>
    </>
  );
}
